// «Расходы» report logic (KOP-39 spec §5.3). Pure: no Vue, no axios. `today` is always injected.
import {
  isoToRu, unixToRu, toNumber, todayIso,
} from './format';
import { readPeriod as readPeriodWith } from './period';

/* ---------- period and query ---------- */

export function defaultPeriod(today) {
  return { from: `${String(today).slice(0, 7)}-01`, to: today };
}

export function readPeriod(query, today) {
  return readPeriodWith(query, defaultPeriod(today));
}

/** Positive integer from `?object=`; null for anything else. */
export function readObjectId(query) {
  const raw = query ? query.object : undefined;
  if (raw === undefined || raw === null || raw === '') return null;
  const s = String(raw);
  if (!/^\d+$/.test(s)) return null;
  const n = Number(s);
  return n > 0 ? n : null;
}

export function expensesQuery(objectId, period) {
  const out = { from: period.from, to: period.to };
  if (objectId) out.object = String(objectId);
  return out;
}

export function sameParams(a, b) {
  return !!a && !!b && !!a.period && !!b.period
    && a.objectId === b.objectId && a.period.from === b.period.from && a.period.to === b.period.to;
}

export function inPeriod(iso, period) {
  return typeof iso === 'string' && !!period && iso >= period.from && iso <= period.to;
}

/* ---------- objects ---------- */

const text = (v) => (v === null || v === undefined ? '' : String(v).trim());
const lower = (v) => text(v).toLowerCase();

export function filterObjects(list, search) {
  const all = Array.isArray(list) ? list.filter(Boolean) : [];
  const q = lower(search);
  if (!q) return all;
  return all.filter((l) => [l.name, l.address, l.code].some((f) => lower(f).includes(q)));
}

export function objectLabel(location) {
  return location ? text(location.name) : '';
}

export function objectInfo(location, result) {
  if (location) return { name: text(location.name), address: text(location.address), code: text(location.code) };
  return { name: text(result && result.name), address: '', code: '' };
}

/* ---------- rows ---------- */

const money = (v) => {
  const n = toNumber(v);
  return n === null ? null : Math.round(n * 100) / 100;
};
const moneyOrZero = (v) => {
  const n = money(v);
  return n === null ? 0 : n;
};
const ISO_DATE_RE = /^(\d{4})-(\d{2})-(\d{2})$/;
const KIND_ORDER = { task: 0, manual: 1 };

export const DESCRIPTION_LIMIT = 15;

/** Display text for a manual expense description: over DESCRIPTION_LIMIT chars → first 15 + «…». */
export function shortDescription(value) {
  const full = text(value);
  const chars = Array.from(full);
  if (chars.length <= DESCRIPTION_LIMIT) return full;
  return `${chars.slice(0, DESCRIPTION_LIMIT).join('')}…`;
}

function taskRow(entry) {
  const d = entry.details && typeof entry.details === 'object' ? entry.details : {};
  const ts = toNumber(entry.completed_at);
  const hasTs = ts !== null && ts > 0;
  return {
    key: `task-${entry.id}`,
    kind: 'task',
    id: Number(entry.id),
    day: hasTs ? todayIso(new Date(ts * 1000)) : null,
    ts: hasTs ? ts : null,
    when: unixToRu(entry.completed_at),
    source: 'Задача',
    taskId: Number(entry.id),
    description: '',
    descriptionTitle: '',
    consumables: money(d.by_consumables),
    works: money(d.by_filed_works), // backend spelling (sic)
    other: moneyOrZero(d.expenses),
    total: moneyOrZero(entry.total),
  };
}

function manualRow(entry) {
  const amount = moneyOrZero(entry.amount);
  const iso = typeof entry.expense_date === 'string' && ISO_DATE_RE.test(entry.expense_date) ? entry.expense_date : null;
  const full = text(entry.description);
  const short = shortDescription(full);
  return {
    key: `manual-${entry.id}`,
    kind: 'manual',
    id: Number(entry.id),
    day: iso,
    ts: null,
    when: isoToRu(iso),
    source: 'Вручную',
    taskId: null,
    // Truncated for the «Источник» column so long descriptions don't stretch it; full text goes to the tooltip.
    description: short,
    descriptionTitle: short === full ? '' : full,
    consumables: null,
    works: null,
    other: amount,
    total: amount,
  };
}

// Newest day first; on the same day tasks (latest closing time first) before manual rows, then the newest id;
// rows without a day last.
function compareRows(a, b) {
  if (a.day !== b.day) {
    if (a.day === null) return 1;
    if (b.day === null) return -1;
    return a.day < b.day ? 1 : -1;
  }
  if (a.kind !== b.kind) return KIND_ORDER[a.kind] - KIND_ORDER[b.kind];
  if (a.ts !== b.ts && a.ts !== null && b.ts !== null) return b.ts - a.ts;
  return b.id - a.id;
}

const list = (group) => (group && Array.isArray(group.details) ? group.details.filter((e) => e && typeof e === 'object') : []);

export function buildRows(result) {
  const d = result && result.details && typeof result.details === 'object' ? result.details : null;
  if (!d) return [];
  return [...list(d.by_tasks).map(taskRow), ...list(d.by_self).map(manualRow)].sort(compareRows);
}

export function summaryModel(result) {
  const d = (result && result.details && typeof result.details === 'object' && result.details) || {};
  return {
    worksTotal: moneyOrZero(d.by_tasks && d.by_tasks.total),
    otherTotal: moneyOrZero(d.by_self && d.by_self.total),
    total: moneyOrZero(result && result.total),
  };
}
